import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider,
  RadioGroup,
  Radio,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Notifications,
  Security,
  Language,
  Palette,
  Storage,
  Help,
  Info,
  Warning,
  CheckCircle,
  Email,
  Sms,
  Schedule,
  EmojiEvents,
  Assignment,
  School,
  Person,
  ExpandMore,
  VolumeUp,
  Brightness4,
  Brightness7,
  Delete,
  Download,
} from '@mui/icons-material';

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  weeklyReports: boolean;
  monthlyReports: boolean;
  achievementAlerts: boolean;
  assignmentReminders: boolean;
  progressUpdates: boolean;
  emergencyAlerts: boolean;
  marketingEmails: boolean;
}

interface PrivacySettings {
  profileVisibility: 'public' | 'private' | 'friends';
  showAchievements: boolean;
  showProgress: boolean;
  allowMessages: boolean;
  dataSharing: boolean;
  analyticsTracking: boolean;
}

interface AppPreferences {
  language: string;
  timezone: string;
  dateFormat: string;
  theme: 'light' | 'dark' | 'auto';
  soundEnabled: boolean;
  autoSave: boolean;
  compactView: boolean;
}

const ParentSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    weeklyReports: true,
    monthlyReports: true,
    achievementAlerts: true,
    assignmentReminders: true,
    progressUpdates: true,
    emergencyAlerts: true,
    marketingEmails: false,
  });
  const [privacy, setPrivacy] = useState<PrivacySettings>({
    profileVisibility: 'private',
    showAchievements: true,
    showProgress: true,
    allowMessages: true,
    dataSharing: false,
    analyticsTracking: true,
  });
  const [preferences, setPreferences] = useState<AppPreferences>({
    language: 'English',
    timezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    theme: 'light',
    soundEnabled: true,
    autoSave: true,
    compactView: false,
  });
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false);
  const [exportDataDialog, setExportDataDialog] = useState(false);

  useEffect(() => {
    // Mock loading settings - replace with API calls
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    // Mock save - replace with API call
    setTimeout(() => {
      setSaving(false);
      alert('Settings saved successfully!');
    }, 1000);
  };

  const handleNotificationChange = (key: keyof NotificationSettings, value: boolean) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
  };

  const handlePrivacyChange = (key: keyof PrivacySettings, value: any) => {
    setPrivacy(prev => ({ ...prev, [key]: value }));
  };

  const handlePreferenceChange = (key: keyof AppPreferences, value: any) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleExportData = () => {
    // Mock data export
    setTimeout(() => {
      alert('Data export initiated. You will receive an email with download link.');
      setExportDataDialog(false);
    }, 1000);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Settings</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Settings
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Customize your experience and manage preferences
            </Typography>
          </Box>
        </Box>
        
        <Button 
          variant="contained" 
          onClick={handleSaveSettings}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Main Settings */}
        <Grid item xs={12} lg={8}>
          {/* Notifications */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Notifications color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notification Preferences
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Communication Methods
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Email />
                      </ListItemIcon>
                      <ListItemText primary="Email Notifications" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.emailNotifications}
                          onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Sms />
                      </ListItemIcon>
                      <ListItemText primary="SMS Notifications" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.smsNotifications}
                          onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Notifications />
                      </ListItemIcon>
                      <ListItemText primary="Push Notifications" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.pushNotifications}
                          onChange={(e) => handleNotificationChange('pushNotifications', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Content Types
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <EmojiEvents />
                      </ListItemIcon>
                      <ListItemText primary="Achievement Alerts" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.achievementAlerts}
                          onChange={(e) => handleNotificationChange('achievementAlerts', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment />
                      </ListItemIcon>
                      <ListItemText primary="Assignment Reminders" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.assignmentReminders}
                          onChange={(e) => handleNotificationChange('assignmentReminders', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <School />
                      </ListItemIcon>
                      <ListItemText primary="Progress Updates" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.progressUpdates}
                          onChange={(e) => handleNotificationChange('progressUpdates', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Schedule />
                      </ListItemIcon>
                      <ListItemText primary="Weekly Reports" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={notifications.weeklyReports}
                          onChange={(e) => handleNotificationChange('weeklyReports', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Privacy & Security */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Security color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Privacy & Security
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Profile Visibility</InputLabel>
                    <Select
                      value={privacy.profileVisibility}
                      label="Profile Visibility"
                      onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="friends">Friends Only</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={privacy.showAchievements}
                        onChange={(e) => handlePrivacyChange('showAchievements', e.target.checked)}
                      />
                    }
                    label="Show Achievements Publicly"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={privacy.showProgress}
                        onChange={(e) => handlePrivacyChange('showProgress', e.target.checked)}
                      />
                    }
                    label="Show Progress to Others"
                    sx={{ mb: 1, display: 'block' }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={privacy.allowMessages}
                        onChange={(e) => handlePrivacyChange('allowMessages', e.target.checked)}
                      />
                    }
                    label="Allow Messages from Teachers"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={privacy.dataSharing}
                        onChange={(e) => handlePrivacyChange('dataSharing', e.target.checked)}
                      />
                    }
                    label="Share Data for Research"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={privacy.analyticsTracking}
                        onChange={(e) => handlePrivacyChange('analyticsTracking', e.target.checked)}
                      />
                    }
                    label="Analytics Tracking"
                    sx={{ mb: 1, display: 'block' }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* App Preferences */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Palette color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  App Preferences
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={preferences.language}
                      label="Language"
                      onChange={(e) => handlePreferenceChange('language', e.target.value)}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Spanish">Español</MenuItem>
                      <MenuItem value="French">Français</MenuItem>
                      <MenuItem value="German">Deutsch</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={preferences.timezone}
                      label="Timezone"
                      onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                    >
                      <MenuItem value="America/New_York">Eastern Time</MenuItem>
                      <MenuItem value="America/Chicago">Central Time</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Date Format</InputLabel>
                    <Select
                      value={preferences.dateFormat}
                      label="Date Format"
                      onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                    >
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Theme</FormLabel>
                    <RadioGroup
                      value={preferences.theme}
                      onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                    >
                      <FormControlLabel value="light" control={<Radio />} label="Light" />
                      <FormControlLabel value="dark" control={<Radio />} label="Dark" />
                      <FormControlLabel value="auto" control={<Radio />} label="Auto" />
                    </RadioGroup>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.soundEnabled}
                        onChange={(e) => handlePreferenceChange('soundEnabled', e.target.checked)}
                      />
                    }
                    label="Sound Effects"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.autoSave}
                        onChange={(e) => handlePreferenceChange('autoSave', e.target.checked)}
                      />
                    }
                    label="Auto-save Progress"
                    sx={{ mb: 1, display: 'block' }}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.compactView}
                        onChange={(e) => handlePreferenceChange('compactView', e.target.checked)}
                      />
                    }
                    label="Compact View"
                    sx={{ mb: 1, display: 'block' }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Account Management
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<Person />}
                  onClick={() => navigate('/parent-profile')}
                  fullWidth
                >
                  Edit Profile
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<Download />}
                  onClick={() => setExportDataDialog(true)}
                  fullWidth
                >
                  Export Data
                </Button>
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteAccountDialog(true)}
                  fullWidth
                >
                  Delete Account
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Help & Support */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Help & Support
              </Typography>
              <List dense>
                <ListItem button>
                  <ListItemIcon>
                    <Help />
                  </ListItemIcon>
                  <ListItemText primary="Help Center" />
                </ListItem>
                <ListItem button>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText primary="Contact Support" />
                </ListItem>
                <ListItem button>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText primary="About StudyStride" />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* System Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                System Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Version:</Typography>
                  <Typography variant="body2">1.0.0</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                  <Typography variant="body2">Jan 15, 2024</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Storage Used:</Typography>
                  <Typography variant="body2">2.3 GB</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Data Dialog */}
      <Dialog open={exportDataDialog} onClose={() => setExportDataDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            We'll prepare a comprehensive export of your account data including:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Profile information" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Children's progress data" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Achievement records" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Message history" />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            You'll receive an email with a download link within 24 hours.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDataDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleExportData}>
            Request Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteAccountDialog} onClose={() => setDeleteAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Delete Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone. All your data will be permanently deleted.
          </Alert>
          <Typography variant="body1" paragraph>
            Are you sure you want to delete your account? This will remove:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Your profile and all personal information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• All children's progress and achievement data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Message history and communications" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Account preferences and settings" />
            </ListItem>
          </List>
          <TextField
            label="Type 'DELETE' to confirm"
            fullWidth
            sx={{ mt: 2 }}
            placeholder="DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ParentSettings;
